import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_URL } from '../apiConfig';

const AdminForgotPasswordVerification = () => {
  const [otp, setOtp] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [resendError, setResendError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendLocked, setResendLocked] = useState(false);

  useEffect(() => {
    const e = searchParams.get('email') || '';
    setEmail(e);
  }, [searchParams]);

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (!resendCooldown) return;
    const interval = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [resendCooldown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!otp || otp.length < 6) {
      setMessage('Please fill out this field with a 6-digit OTP.');
      return;
    }
    setLoading(true);
    setMessage('');
    try {
      const res = await fetch(`${API_URL}/admin-forgot-password-verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp })
      });
      const data = await res.json();
      if (!res.ok) {
        setMessage(data.message || 'Invalid or expired OTP');
      } else {
        navigate(`/admin/reset-password?email=${encodeURIComponent(email)}&otp=${encodeURIComponent(otp)}`);
      }
    } catch (err) {
      console.error(err);
      setMessage('Server error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendError('');
    setResendMessage('');
    if (!email) { setResendError('Missing email. Please go back.'); return; }

    setResendLoading(true);
    try {
      const res = await fetch(`${API_URL}/admin-resend-forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await res.json();
      if (!res.ok) {
        setResendError(data.message || 'Unable to resend. Try again later.');
        if (data.cooldownSeconds) {
          setResendCooldown(data.cooldownSeconds);
          // If cooldown is > 5 minutes it's a lockout
          if (data.cooldownSeconds > 300) setResendLocked(true);
        }
        return;
      }
      setResendMessage('A new code has been sent to your email.');
      if (data.cooldownSeconds) setResendCooldown(data.cooldownSeconds);
      setResendLocked(false);
    } catch (err) {
      console.error(err);
      setResendError('Server error. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <header style={styles.topHeader}>
        <img src={require("../assets/logonobg.png")} alt="Logo" style={styles.logo} />
      </header>

      <div style={styles.content}>
        <div style={styles.card}>
          <div style={styles.iconContainer}>
            <div style={styles.iconCircle}>
              <span style={styles.icon}>🔑</span>
            </div>
          </div>
          <h2 style={styles.titleMain}>Enter OTP</h2>
          <p style={styles.subtitle}>
            We've sent a one-time code to: <br />
            <strong style={{ color: "#2E4B4C", fontSize: "15px" }}>{email}</strong>
          </p>

          <form onSubmit={handleSubmit}>
            <div style={{ textAlign: "left", marginBottom: "8px" }}>
              <label style={styles.label}>6-Digit OTP</label>
            </div>
            <input
              type="text"
              placeholder="000000"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              style={{ ...styles.input, marginBottom: message ? "8px" : "24px" }}
              maxLength="6"
            />
            {message && <div style={{...styles.errorText, textAlign: 'left'}}>{message}</div>}
            <button type="submit" style={styles.button} disabled={loading}>
              {loading ? 'Verifying...' : 'Verify OTP'}
            </button>
          </form>

          {/* Resend Section */}
          <div style={{ marginTop: '20px', textAlign: 'center' }}>
            {resendMessage && (
              <div style={{ color: '#059669', fontSize: '13px', marginBottom: '12px', fontWeight: '500' }}>
                {resendMessage}
              </div>
            )}
            {resendError && (
              <div style={{ color: '#e74c3c', fontSize: '13px', marginBottom: '12px', fontWeight: '500' }}>
                {resendError}
              </div>
            )}
            <button
              type="button"
              onClick={handleResend}
              disabled={resendLoading || resendCooldown > 0 || resendLocked}
              style={{
                backgroundColor: 'transparent',
                color: (resendCooldown > 0 || resendLocked) ? '#9ca3af' : '#2E4B4C',
                border: `1px solid ${(resendCooldown > 0 || resendLocked) ? '#d1d5db' : '#2E4B4C'}`,
                padding: '10px 20px',
                borderRadius: '10px',
                cursor: (resendLoading || resendCooldown > 0 || resendLocked) ? 'not-allowed' : 'pointer',
                opacity: (resendLoading || resendCooldown > 0 || resendLocked) ? 0.65 : 1,
                fontSize: '13px',
                fontWeight: '600',
                transition: 'all 0.2s',
                fontFamily: "'Montserrat', sans-serif",
              }}
            >
              {resendLoading
                ? 'Sending...'
                : resendCooldown > 0
                  ? `Resend Code (${resendCooldown}s)`
                  : 'Resend Code'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: "100vh",
    backgroundColor: "#f9fafb",
    fontFamily: "'Montserrat', sans-serif",
  },
  topHeader: {
    backgroundColor: '#14291F',
    height: '60px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)"
  },
  logo: {
    height: '45px',
    objectFit: 'contain'
  },
  content: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "60px 20px",
  },
  card: {
    width: '100%',
    maxWidth: '440px',
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    border: '1px solid #e5e7eb',
    padding: '48px 40px',
    textAlign: 'center',
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.05), 0 10px 10px -5px rgba(0, 0, 0, 0.02)",
  },
  iconContainer: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "24px",
  },
  iconCircle: {
    width: "80px",
    height: "80px",
    backgroundColor: "#fef3c7",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  icon: {
    fontSize: "36px",
  },
  titleMain: {
    fontFamily: "'Abril Fatface', serif",
    fontSize: '32px',
    color: '#2E4B4C',
    margin: '0 0 12px 0',
    letterSpacing: "0.5px"
  },
  subtitle: {
    fontSize: '14px',
    color: '#6b7280',
    marginBottom: '32px',
    lineHeight: '1.6',
  },
  label: {
    color: "#4b5563",
    fontWeight: "600",
    fontSize: "12px",
    textTransform: "uppercase",
    letterSpacing: "1px",
    marginBottom: "8px",
    display: "inline-block"
  },
  input: {
    width: "100%",
    padding: "16px",
    fontSize: "28px",
    letterSpacing: "12px",
    textAlign: "center",
    border: "2px solid #e5e7eb",
    borderRadius: "12px",
    boxSizing: "border-box",
    marginBottom: "24px",
    fontWeight: "800",
    color: "#14291F",
    outline: "none",
    transition: "all 0.2s",
    backgroundColor: "#f9fafb"
  },
  button: {
    backgroundColor: "#14291F",
    color: "#fff",
    padding: "16px 30px",
    border: "none",
    borderRadius: "12px",
    cursor: "pointer",
    fontSize: "15px",
    fontWeight: "700",
    width: "100%",
    transition: "all 0.2s",
    textTransform: "uppercase",
    letterSpacing: "1px",
    boxShadow: "0 4px 12px rgba(20, 41, 31, 0.15)"
  },
  errorText: {
    color: "#e74c3c",
    fontSize: "12px",
    marginBottom: "16px",
    fontWeight: "500",
    letterSpacing: "0.3px"
  },
};

export default AdminForgotPasswordVerification;

